import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const LINKS = [
  { id: "#services", key: "services" },
  { id: "#stack", key: "stack" },
  { id: "#why", key: "why" },
  { id: "#contact", key: "contact" },
];

const LANGS = ["en", "it", "de", "fr"];

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    if (document.querySelector(id)) {
      scrollToId(id);
    } else {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/60 bg-slate-950/70 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo onClick={() => go("#top")} />

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-link-${l.key}`}
              onClick={(e) => {
                e.preventDefault();
                go(l.id);
              }}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-orange-300"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          {/* Language switcher lives in the navbar only on tablet/desktop —
              on mobile it moves inside the hamburger menu to keep the bar clean. */}
          <div
            className="hidden items-center gap-0.5 rounded-full border border-slate-700/50 bg-slate-900/60 p-1 md:flex"
            data-testid="language-switcher"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`rounded-full px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors duration-200 ${
                  lang === l ? "bg-orange-400 font-semibold text-slate-950" : "text-slate-400 hover:text-orange-300"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <Button
            data-testid="nav-cta"
            onClick={() => go("#contact")}
            className="hidden rounded-full bg-orange-400 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_0_24px_rgba(251,146,60,0.35)] transition-all duration-300 hover:scale-[1.04] hover:bg-orange-300 hover:shadow-[0_0_36px_rgba(251,146,60,0.5)] sm:inline-flex"
          >
            {t.nav.cta}
          </Button>

          <button
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-700/50 text-slate-300"
            data-testid="nav-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              <div
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-800/60 bg-slate-900/40 px-4 py-3"
                data-testid="language-switcher-mobile"
              >
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  <Languages className="h-4 w-4 text-orange-300" />
                </span>
                <div className="flex items-center gap-0.5 rounded-full border border-slate-700/50 bg-slate-950/60 p-1">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      data-testid={`mlang-${l}`}
                      onClick={() => setLang(l)}
                      aria-pressed={lang === l}
                      className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors duration-200 ${
                        lang === l ? "bg-orange-400 font-semibold text-slate-950" : "text-slate-400 hover:text-orange-300"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  onClick={(e) => {
                    e.preventDefault();
                    go(l.id);
                  }}
                  className="block rounded-lg px-3 py-3 text-base font-medium text-slate-200 transition-colors hover:bg-slate-900 hover:text-orange-300"
                >
                  {t.nav[l.key]}
                </a>
              ))}
              <div className="pt-2">
                <Button
                  onClick={() => go("#contact")}
                  className="w-full rounded-full bg-orange-400 py-3 text-sm font-semibold text-slate-950 hover:bg-orange-300"
                >
                  {t.nav.cta}
                </Button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
